import React, { useEffect } from 'react';
import { Box, Chip, Typography, Stack, Tooltip } from '@mui/material';
import { useOptionsStore } from '../../store/useOptionsStore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import BoltIcon from '@mui/icons-material/Bolt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export const OptionsHeaderBar: React.FC = () => {
  const {
    spotData,
    activePosition,
    todayPnl,
    marginUtilizationPct,
    riskStatus,
    killSwitchStatus,
    modeConfig,
    healthReport,
    isEntryAllowed,
    fetchErrors,
    fetchSpotData,
    fetchKillSwitch,
    fetchModeConfig,
    fetchHealthReport,
  } = useOptionsStore();
  const errorCount = Object.keys(fetchErrors).length;

  useEffect(() => {
    fetchSpotData();
    fetchKillSwitch();
    fetchModeConfig();
    fetchHealthReport();

    const timer = setInterval(() => {
      fetchSpotData();
      fetchKillSwitch();
      fetchHealthReport();
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const spot = spotData?.spot || 0;
  const gapPct = spotData?.gapPct ?? spotData?.dayChangePct ?? 0;
  const isPositive = gapPct >= 0;
  const killLevel = killSwitchStatus?.currentLevel || null;
  const isKillActive = !!killLevel && killLevel !== 'LEVEL_0_ALL_SYSTEMS_GO';

  const mode = modeConfig?.mode || null;
  const getModeBadge = () => {
    switch (mode) {
      case null:
        return {
          label: '⚪ CHECKING MODE…',
          bg: '#F1F5F9',
          color: '#64748B',
          border: '1px solid #CBD5E1',
        };
      case 'LIVE':
        return {
          label: '🔴 LIVE TRADING',
          bg: '#FEE2E2',
          color: '#DC2626',
          border: '1px solid #EF4444',
        };
      case 'PAPER':
        return {
          label: '🟠 PAPER TRADING',
          bg: '#FFEDD5',
          color: '#EA580C',
          border: '1px solid #F97316',
        };
      case 'OBSERVE':
        return {
          label: '🔵 OBSERVE MODE',
          bg: '#DBEAFE',
          color: '#2563EB',
          border: '1px solid #3B82F6',
        };
      case 'SHADOW':
      default:
        return {
          label: '🟡 SHADOW (ZERO RISK)',
          bg: '#FEF9C3',
          color: '#CA8A04',
          border: '1px solid #EAB308',
        };
    }
  };

  const modeBadge = getModeBadge();

  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        color: '#0F172A',
        px: 2.5,
        py: 1.5,
        borderRadius: 2.5,
        mb: 3,
        border: mode === 'LIVE' ? '2px solid #EF4444' : '1px solid #E2E8F0',
        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.06)',
      }}
    >
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems="center" spacing={2}>
        {/* Spot & Market Health */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, letterSpacing: 0.5 }}>
              NIFTY 50 SPOT
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontFamily: "'Roboto Mono', monospace" }}>
                {spot > 0 ? `₹${spot.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}` : '— (Offline)'}
              </Typography>
              {spotData && spot > 0 && (
                <Chip
                  size="small"
                  icon={<TrendingUpIcon sx={{ fontSize: '14px !important', color: isPositive ? '#16A34A' : '#DC2626' }} />}
                  label={`${isPositive ? '+' : ''}${gapPct.toFixed(2)}%`}
                  sx={{
                    bgcolor: isPositive ? '#DCFCE7' : '#FEE2E2',
                    color: isPositive ? '#16A34A' : '#DC2626',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    height: 22,
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Operational Mode Banner */}
          <Chip
            size="small"
            label={modeBadge.label}
            sx={{
              bgcolor: modeBadge.bg,
              color: modeBadge.color,
              fontWeight: 800,
              fontSize: '0.72rem',
              letterSpacing: 0.5,
              border: modeBadge.border,
              height: 26,
            }}
          />
        </Stack>

        {/* Active Strategy & Day's P&L */}
        <Stack direction="row" alignItems="center" spacing={2.5}>
          <Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
              STRATEGY / STATUS
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: activePosition ? '#16A34A' : '#94A3B8' }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0284C7' }}>
                {activePosition ? activePosition.strategyName.replace(/_/g, ' ') : 'NO ACTIVE STRATEGY'}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
              TODAY'S P&L
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontFamily: "'Roboto Mono', monospace",
                color: todayPnl >= 0 ? '#16A34A' : '#DC2626',
              }}
            >
              {todayPnl >= 0 ? '+' : ''}₹{todayPnl.toLocaleString('en-IN')}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
              MARGIN UTILIZED
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: marginUtilizationPct > 60 ? '#D97706' : '#334155' }}>
              {marginUtilizationPct}%
            </Typography>
          </Box>
        </Stack>

        {/* Entry Lockout, Risk & Kill Switch Status */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Tooltip title={isEntryAllowed ? 'System healthy: entries permitted' : healthReport?.lockoutReason || 'Safety lockout active'}>
            <Chip
              size="small"
              icon={isEntryAllowed ? <LockOpenIcon sx={{ fontSize: '13px !important', color: '#16A34A' }} /> : <LockIcon sx={{ fontSize: '13px !important', color: '#DC2626' }} />}
              label={isEntryAllowed ? 'ENTRIES ALLOWED' : 'ENTRIES LOCKED'}
              sx={{
                bgcolor: isEntryAllowed ? '#DCFCE7' : '#FEE2E2',
                color: isEntryAllowed ? '#16A34A' : '#DC2626',
                fontWeight: 700,
                fontSize: '0.68rem',
                border: isEntryAllowed ? '1px solid #86EFAC' : '1px solid #FCA5A5',
              }}
            />
          </Tooltip>

          <Tooltip title={`Risk Status: ${riskStatus}`}>
            <Chip
              size="small"
              icon={<ShieldIcon sx={{ fontSize: '13px !important', color: riskStatus === 'NORMAL' ? '#16A34A' : '#D97706' }} />}
              label={`RISK: ${riskStatus}`}
              sx={{
                bgcolor: riskStatus === 'NORMAL' ? '#F0FDF4' : '#FEF3C7',
                color: riskStatus === 'NORMAL' ? '#16A34A' : '#D97706',
                border: riskStatus === 'NORMAL' ? '1px solid #BBF7D0' : '1px solid #FDE68A',
                fontWeight: 700,
                fontSize: '0.70rem',
              }}
            />
          </Tooltip>

          <Tooltip title={`Kill-Switch Level: ${killLevel || 'Unknown — not yet loaded'}`}>
            <Chip
              size="small"
              icon={<BoltIcon sx={{ fontSize: '13px !important', color: isKillActive ? '#DC2626' : '#0284C7' }} />}
              label={!killLevel ? 'KILL: CHECKING…' : isKillActive ? killLevel.replace(/_/g, ' ') : 'KILL: LEVEL 0'}
              sx={{
                bgcolor: isKillActive ? '#FEE2E2' : '#E0F2FE',
                color: isKillActive ? '#DC2626' : '#0284C7',
                fontWeight: 700,
                fontSize: '0.70rem',
                border: isKillActive ? '1px solid #EF4444' : '1px solid #BAE6FD',
              }}
            />
          </Tooltip>

          {errorCount > 0 && (
            <Tooltip title={Object.entries(fetchErrors).map(([k, v]) => `${k}: ${v}`).join(' • ')}>
              <Chip
                size="small"
                icon={<WarningAmberIcon sx={{ fontSize: '13px !important', color: '#DC2626' }} />}
                label={`${errorCount} FEED${errorCount > 1 ? 'S' : ''} UNREACHABLE`}
                sx={{
                  bgcolor: '#FEE2E2',
                  color: '#DC2626',
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  border: '1px solid #EF4444',
                }}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
